/* eslint-disable camelcase */
import React, {
} from 'react';
import {
  Formik,
  Field,
} from 'formik';
import {
  useDispatch,
} from 'react-redux';
import i18n from 'i18next';

import {
  WorkoutType,
} from '@gymcloud/core';

import {
  Video,
  Button,
  Author,
  TagList,
  TagField,
  TextField,
  EditorField,
  MarkdownRenderer,
  WorkoutExercisesList,
  WorkoutExercisesFooter,
  WorkoutExercisesSubHeader,
} from 'web-components';
import {
  commonActions,
} from 'web-actions';

import {
  Flex,
  H100,
  WorkoutExercisesView,
} from 'web-styled';

import * as S from './styled';

type Props = {
  type?: string,
  isPro?: boolean,
  workout: any,
  readOnly?: boolean,
  workoutType: WorkoutType,
  resourceType: 'personalWorkouts' | 'warmups' | 'cooldowns',
  workoutAuthor?: any,

  onEdit: { (): void },
  onSubmit: { (data: any, actions: any): void },
  onRemove?: { (): void },
  onCancel?: { (): void },
  onEnterResult?: { (): void },
  onSaveToLibrary?: { (): void },
};

const PersonalWorkout = ({
  type = 'workout',
  isPro = false,
  workout,
  readOnly = false,
  workoutType,
  resourceType,
  workoutAuthor,

  onEdit,
  onSubmit,
  onRemove,
  onCancel,
  onEnterResult,
  onSaveToLibrary,
}: Props) => {
  const dispatch = useDispatch();
  return (
    <Flex fillSpace column spacing={20}>
      <Formik
        initialValues={{
          name: workout?.name || '',
          tag_list: workout?.tag_list ? workout.tag_list.join(',') : '',
          video_id: workout?.video_id || '',
          video_url: workout?.video_url || '',
          description: workout?.description || '',
        }}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Flex
            as="form"
            column
            spacing={20}
            width="100%"
            shrink="0"
            onSubmit={formikProps.handleSubmit}
          >
            <S.Header>
              <Author
                fullName={workout?.author_full_name}
                avatar={workoutAuthor?.user_profile?.avatar?.thumb?.url}
              />
              {readOnly ? (
                <Flex spacing={10}>
                  {onEnterResult && (
                    <Button
                      ghost
                      appearance="primary"
                      onClick={onEnterResult}
                    >
                      Enter Results
                    </Button>
                  )}
                  {onSaveToLibrary && (
                    <Button
                      ghost
                      appearance="primary"
                      onClick={onSaveToLibrary}
                    >
                      Save as Template
                    </Button>
                  )}
                  <Button
                    ghost
                    appearance="primary"
                    onClick={() => {
                      window.print();
                      dispatch(commonActions.triggerAnalytic('clicked_print_button'));
                    }}
                  >
                    Print
                  </Button>
                  {isPro && (
                    <Button
                      ghost
                      appearance="primary"
                      onClick={onEdit}
                    >
                      Edit
                    </Button>
                  )}
                </Flex>
              ) : (
                <Flex spacing={10}>
                  <Button
                    ghost
                    type="button"
                    appearance="primary"
                    isLoading={formikProps.isSubmitting}
                    isDisabled={formikProps.isSubmitting}
                    onClick={async () => {
                      if (formikProps.dirty) {
                        formikProps.resetForm({ values: formikProps.values });
                        await formikProps.submitForm();
                      } else {
                        onCancel?.();
                      }
                    }}
                  >
                    Done
                  </Button>
                  {onRemove && (
                    <Button
                      ghost
                      appearance="danger"
                      onClick={onRemove}
                    >
                      Remove
                    </Button>
                  )}
                </Flex>
              )}
            </S.Header>
            <S.WorkoutDescription
              spacing={30}
            >
              <Flex
                column
                flex="0 0 35%"
                align="center"
              >
                <Video
                  {...(readOnly
                    ? {
                      id: workout.video_id,
                      src: workout.video_url,
                      readOnly: true,
                    } : {
                      id: formikProps.values.video_id,
                      src: formikProps.values.video_url,
                    }
                  )}
                  title={workout?.name}
                  height="250px"
                  buildQuery={() => (document.getElementById('name-id') as HTMLInputElement)?.value || ''}
                  resourceType={resourceType}
                  onAssign={(video) => {
                    const id = video.embed_url.indexOf('vimeo') !== -1 ? video.id : null;
                    formikProps.setFieldValue('video_id', id);
                    formikProps.setFieldValue('video_url', video.embed_url);
                  }}
                  onUploaded={(videos) => {
                    formikProps.setFieldValue('video_id', videos[0].id);
                    formikProps.setFieldValue('video_url', '');
                  }}
                  onDelete={() => {
                    formikProps.setFieldValue('video_id', '');
                    formikProps.setFieldValue('video_url', '');
                  }}
                />
              </Flex>
              {readOnly ? (
                <Flex
                  column
                  spacing={15}
                  flex="1 1 auto"
                >
                  <H100 color="primary">
                    {workout.name}
                  </H100>

                  <MarkdownRenderer
                    source={workout?.description}
                    maxHeight={180}
                    placeholder={i18n.t('click.to.add.description', type ? { type: i18n.t(type) } : {})}
                  />
                  <TagList
                    tags={workout?.tag_list}
                    resourceType="workouts"
                  />
                </Flex>
              ) : (
                <Flex
                  column
                  spacing={15}
                  flex="1 1 auto"
                >
                  <Field
                    flex="0 0 60px"
                    name="name"
                    label="Name"
                    color="primary"
                    component={TextField}
                  />
                  <Field
                    name="description"
                    color="primary"
                    label="Description"
                    minRows={5}
                    placeholder={i18n.t('type.description.placeholder', type ? { type: i18n.t(type) } : {})}
                    component={EditorField}
                  />
                  <Field
                    name="tag_list"
                    component={TagField}
                  />
                </Flex>
              )}
            </S.WorkoutDescription>
          </Flex>
        )}
      </Formik>
      <S.ExerciseListWrapper>
        <WorkoutExercisesView>
          {!readOnly && (
            <WorkoutExercisesSubHeader
              workoutId={workout.id}
            />
          )}

          <WorkoutExercisesList
            type={type || 'workout'}
            readOnly={readOnly}
            workoutId={workout.id}
            workoutType={workoutType}
          />

          {!readOnly && (
            <WorkoutExercisesFooter
              workoutId={workout.id}
              workoutType={workoutType}
            />
          )}
        </WorkoutExercisesView>
      </S.ExerciseListWrapper>
    </Flex>
  );
};

export default PersonalWorkout;
